import React from 'react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { timelineMarkerLeftStyle } from './timelineLayout';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type DotTone = 'error' | 'warn' | 'neutral' | 'success' | 'violet';

export function isErrorOrWarnTone(tone: DotTone): boolean {
  return tone === 'error' || tone === 'warn';
}

export function dotToneClasses(tone: DotTone): string {
  switch (tone) {
    case 'error':
      return 'bg-red-400';
    case 'warn':
      return 'bg-amber-400';
    case 'violet':
      return 'bg-violet-400 shadow-[0_0_6px_rgba(167,139,250,0.4)]';
    case 'success':
      return 'bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.35)]';
    default:
      return 'bg-sky-400/90 shadow-[0_0_5px_rgba(56,189,248,0.35)]';
  }
}

export type HoverTipState = { x: number; y: number; lines: string[]; placeAbove: boolean } | null;

export function TrackPlayheadLine({ pct }: { pct: number }) {
  return (
    <div
      className="pointer-events-none absolute inset-y-0 z-[55] w-px bg-rose-500/90 shadow-[0_0_6px_rgba(251,113,133,0.4)]"
      style={{ ...timelineMarkerLeftStyle(pct), transform: 'translateX(-50%)' }}
    />
  );
}

export function TimelineDot({
  leftPct,
  zIndex,
  tone,
  playheadActive,
  isSelected,
  isFirstFailure,
  dimmed,
  isCorrelated,
  tipLines,
  ariaLabel,
  onSelect,
  onHoverTip,
}: {
  leftPct: number;
  zIndex: number;
  tone: DotTone;
  playheadActive: boolean;
  isSelected: boolean;
  isFirstFailure: boolean;
  dimmed: boolean;
  isCorrelated: boolean;
  tipLines: string[];
  ariaLabel: string;
  onSelect: (e: React.MouseEvent) => void;
  onHoverTip: (tip: HoverTipState) => void;
}) {
  const title = tipLines.join('\n');
  const isError = tone === 'error';
  const isWarn = tone === 'warn';
  const hitSize = isError ? 'h-6 w-6' : isWarn ? 'h-5 w-5' : 'h-5 w-5';
  const dotSize = isError ? 'h-3 w-3' : isWarn ? 'h-2.5 w-2.5' : 'h-2 w-2';
  const buttonScale = playheadActive ? 1.05 : 1;
  const playheadOrFailureOutline =
    isFirstFailure && isError
      ? 'outline outline-2 outline-offset-0 outline-red-200/60'
      : playheadActive
        ? 'outline outline-2 outline-offset-0 outline-rose-300/75'
        : '';
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      aria-current={isSelected ? ('true' as const) : undefined}
      title={title}
      style={{
        ...timelineMarkerLeftStyle(leftPct),
        transform: `translate(-50%, -50%) scale(${buttonScale})`,
        zIndex,
      }}
      className={cn(
        'group absolute top-1/2 flex items-center justify-center rounded-full border border-[var(--tl-dot-border)] bg-[var(--tl-dot-bg)] outline-none transition-[transform,opacity] hover:border-[var(--tl-dot-border-hover)] focus-visible:ring-2 focus-visible:ring-sky-400/80',
        hitSize,
        dimmed && 'opacity-[0.28]',
        isSelected && 'border-sky-400/70 bg-sky-500/15 shadow-[0_0_0_1px_rgba(56,189,248,0.25)]',
        isCorrelated && 'ring-1 ring-inset ring-amber-400/70',
      )}
      onClick={onSelect}
      onPointerDown={(e) => {
        e.stopPropagation();
        onHoverTip(null);
      }}
      onPointerEnter={(e) => {
        const el = e.currentTarget;
        const r = el.getBoundingClientRect();
        const spaceAbove = r.top;
        const placeAbove = spaceAbove > 100;
        onHoverTip({
          x: r.left + r.width / 2,
          y: placeAbove ? r.top : r.bottom,
          lines: tipLines,
          placeAbove,
        });
      }}
      onPointerLeave={() => onHoverTip(null)}
    >
      <span
        className={cn(
          'relative shrink-0 rounded-full transition-[height,width,transform] duration-150 ease-out',
          'group-hover:scale-[1.2] group-active:scale-95',
          dotToneClasses(tone),
          dotSize,
          isError && 'detrix-error-pulse',
          isWarn && 'detrix-warn-pulse',
          isSelected && !isError && 'h-2.5 w-2.5',
          playheadOrFailureOutline,
        )}
      />
    </button>
  );
}

export function HoverTipOverlay({ tip }: { tip: HoverTipState }) {
  if (!tip) return null;
  return (
    <div
      className="pointer-events-none fixed z-[10000] max-w-[min(22rem,calc(100vw-1.5rem))] break-words rounded-md border border-[var(--tl-tip-border)] bg-[var(--tl-tip-bg)] px-2.5 py-2 text-left text-[10px] leading-snug text-[var(--tl-tip-body)] shadow-[var(--tl-tip-shadow)] backdrop-blur-sm"
      style={{
        left: tip.x,
        top: tip.y,
        transform: tip.placeAbove ? 'translate(-50%, calc(-100% - 8px))' : 'translate(-50%, 8px)',
      }}
    >
      {tip.lines.map((line, i) => {
        const headlineError = i === 0 && line === 'ERROR';
        const headlineWarn = i === 0 && line === 'WARNING';
        return (
          <div
            key={i}
            className={cn(
              i === 0 && !headlineError && !headlineWarn && 'font-semibold text-[var(--tl-tip-head)]',
              i === 0 && headlineError && 'text-[11px] font-bold uppercase tracking-[0.12em] text-red-400',
              i === 0 && headlineWarn && 'text-[11px] font-bold uppercase tracking-[0.12em] text-amber-300',
              i !== 0 && 'text-[var(--tl-tip-body)]',
            )}
          >
            {line}
          </div>
        );
      })}
    </div>
  );
}
